import { useCallback, useEffect, useRef, useState } from 'react';
import { GameFrame, useGame, useRoundStyle } from '../sdk/react';

const FEEDBACK_HOLD_MS = 700;

type Prompt = { prompt: string; hint?: string };

type Round = {
  roundId: string | null;
  promptText: string;
  promptType: 'AUDIO' | 'TEXT';
  hint: string | null;
  probe: boolean;
};

export default function SupervisorScored() {
  const game = useGame();
  const { kit } = game;
  const live = kit.rounds.available;
  const list = (Array.isArray(game.config.prompts) ? game.config.prompts : [])
    .filter((p): p is Prompt => !!p && typeof (p as Prompt).prompt === 'string' && (p as Prompt).prompt.trim() !== '');
  const empty = !live && list.length === 0;
  const [round, setRound] = useState<Round | null>(null);
  const [marked, setMarked] = useState<'CORRECT' | 'INCORRECT' | null>(null);
  const [right, setRight] = useState(0);
  const [wrong, setWrong] = useState(0);
  const [fading, setFading] = useState(false);
  const shownAt = useRef(0);
  const prompts = useRef(0);
  const next = useRef(0);
  const { style, typeClass } = useRoundStyle();

  const start = useCallback(async () => {
    setMarked(null);
    kit.speech.stop();
    if (live) {
      const r = await kit.rounds.start();
      if (!r) return;
      kit.session.offerBreak(r.breakOffered === true);
      setRound({
        roundId: typeof r.roundId === 'string' ? r.roundId : null,
        promptText: typeof r.promptText === 'string' ? r.promptText : '',
        promptType: r.promptType === 'TEXT' ? 'TEXT' : 'AUDIO',
        hint: typeof r.hint === 'string' ? r.hint : null,
        probe: r.probe === true,
      });
      return;
    }
    if (list.length === 0) return;
    const p = list[next.current % list.length];
    next.current += 1;
    setRound({ roundId: null, promptText: p.prompt, promptType: game.textPrompt ? 'TEXT' : 'AUDIO', hint: p.hint ?? null, probe: false });
  }, [kit, live, game.config]);

  useEffect(() => { void start(); }, [start]);

  const playPrompt = useCallback(() => {
    if (!round) return;
    prompts.current += 1;
    if (round.promptType === 'AUDIO') kit.speech.say(round.promptText);
  }, [kit, round]);
  const playRef = useRef(playPrompt);
  playRef.current = playPrompt;

  useEffect(() => {
    if (!round) return;
    shownAt.current = kit.clock.now();
    prompts.current = 0;
    const t = window.setTimeout(() => playRef.current(), 350);
    return () => window.clearTimeout(t);
  }, [round]);

  useEffect(() => {
    if (!marked) return;
    const fade = window.setTimeout(() => setFading(true), FEEDBACK_HOLD_MS);
    const advance = window.setTimeout(() => { void start().finally(() => setFading(false)); }, FEEDBACK_HOLD_MS + game.transitionMs);
    return () => { window.clearTimeout(fade); window.clearTimeout(advance); };
  }, [marked]);

  async function score(ok: boolean) {
    if (!round || marked) return;
    kit.speech.stop();
    let correct = ok;
    if (round.roundId && live) {
      const result = await kit.rounds.attempt(round.roundId, {
        tile: ok ? 'CORRECT' : 'INCORRECT',
        respondedMs: Math.round(kit.clock.elapsedSince(shownAt.current)),
        promptsPlayed: prompts.current,
      });
      if (!result) return;
      correct = result.correct === true;
    } else if (ok) kit.sound.correct();
    if (correct) setRight((n) => n + 1);
    else setWrong((n) => n + 1);
    setMarked(ok ? 'CORRECT' : 'INCORRECT');
  }

  return (
    <GameFrame game={game} empty={empty} emptyText="Edit it to add prompts for the adult to give." onResume={() => { void start(); }} breakTrial={round?.roundId ?? null}>
      {round && (
        <>
          {round.probe && (
            <div className="banner" role="status">
              First go today, so no help is given and nothing is earned. It shows what they can do on their own.
            </div>
          )}
          <div className={`card ${typeClass}${fading ? ' fading-out' : ''}`} style={style}>
            <div className="muted" style={{ marginBottom: 6 }}>✓ {right}{' · '}✗ {wrong}</div>
            <div className="concept-prompt-big">{round.promptText}</div>
            {round.hint && <div className="muted" style={{ textAlign: 'center', marginBottom: 12 }}>{round.hint}</div>}
            <div className="toolbar" style={{ justifyContent: 'center' }}>
              <button className="btn" onClick={() => void score(true)} disabled={!!marked} aria-label="Mark correct">✓ Correct</button>
              <button className="btn secondary" onClick={() => void score(false)} disabled={!!marked} aria-label="Mark incorrect">✗ Incorrect</button>
              <button className="btn ghost" onClick={() => playRef.current()} disabled={!!marked} aria-label="Give the prompt again">Say it again</button>
            </div>
          </div>
        </>
      )}
    </GameFrame>
  );
}
